import { useMemo } from 'react';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { SlaExpandedDetailItem } from '~/app/landing/sla/shared/components/SlaExpandedDetailGrid';
import { formatDateLabel } from '~/app/landing/sla/shared/utils/sla-format.utils';
import type {
  MaintenancePartnershipValidity,
  MaintenanceSlaContractRecord,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { MAINTENANCE_WEEKDAYS } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { minutesToDurationParts } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/rules';
import { interventionModeLabelKey } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/rules';

export function useMaintenanceSlaHeaderItems(
  contract: MaintenanceSlaContractRecord,
  partnership: MaintenancePartnershipValidity,
): SlaExpandedDetailItem[] {
  const { t } = useTranslation('sla');

  return useMemo(() => {
    const dash = t('oems.maintenance.dash');
    const restored =
      contract.restoredAvailableMinutes == null
        ? dash
        : t('oems.maintenance.restoredRead', {
            ...minutesToDurationParts(contract.restoredAvailableMinutes),
            minutes: String(
              minutesToDurationParts(contract.restoredAvailableMinutes).minutes,
            ).padStart(2, '0'),
          });

    const dayLabels = MAINTENANCE_WEEKDAYS.filter((day) =>
      contract.workingDays.includes(day),
    ).map((day) => t(`oems.maintenance.days.${day}`));

    let businessHours = dash;
    if (contract.businessHoursStart && contract.businessHoursEnd) {
      businessHours = t('oems.maintenance.businessHoursRange', {
        start: contract.businessHoursStart,
        end: contract.businessHoursEnd,
      });
    } else if (contract.businessHoursStart || contract.businessHoursEnd) {
      businessHours =
        contract.businessHoursStart ?? contract.businessHoursEnd ?? dash;
    }

    const rawMode = contract.interventionMode ?? 'BOTH';
    const interventionModeLabel = t(interventionModeLabelKey(rawMode));
    const interventionColor =
      rawMode === 'ONSITE'
        ? 'blue'
        : rawMode === 'REMOTE'
          ? 'purple'
          : 'geekblue';

    return [
      {
        key: 'validFrom',
        label: t('oems.maintenance.fields.validFrom'),
        value: formatDateLabel(partnership.validFrom),
      },
      {
        key: 'validTo',
        label: t('oems.maintenance.fields.validTo'),
        value: partnership.validTo
          ? formatDateLabel(partnership.validTo)
          : t('review.openEnded'),
      },
      {
        key: 'interventionMode',
        label: t('oems.maintenance.fields.interventionMode'),
        value: (
          <Tag color={interventionColor} style={{ marginInlineEnd: 0 }}>
            {interventionModeLabel}
          </Tag>
        ),
      },
      {
        key: 'timezone',
        label: t('oems.maintenance.fields.timezone'),
        value: contract.timezone,
      },
      {
        key: 'businessHours',
        label: t('oems.maintenance.fields.businessHours'),
        value: businessHours,
      },
      {
        key: 'workingDays',
        label: t('oems.maintenance.fields.workingDays'),
        value: dayLabels.length > 0 ? dayLabels.join(', ') : dash,
      },
      {
        key: 'holiday',
        label: t('oems.maintenance.fields.holidayCalendarCode'),
        value: contract.holidayCalendarCode,
      },
      {
        key: 'excludeHolidays',
        label: t('oems.maintenance.fields.excludeHolidays'),
        value: contract.excludeHolidays
          ? t('oems.maintenance.yes')
          : t('oems.maintenance.no'),
      },
      {
        key: 'currency',
        label: t('oems.fields.currency'),
        value: contract.priceCurrency || 'EUR',
      },
      {
        key: 'exclusions',
        label: t('oems.maintenance.fields.exclusions'),
        value: contract.exclusionsText ?? dash,
      },
      {
        key: 'recurrence',
        label: t('oems.maintenance.fields.recurrenceWindowDays'),
        value: contract.recurrenceWindowDays ?? dash,
      },
      {
        key: 'restored',
        label: t('oems.maintenance.fields.restoredAvailable'),
        value: restored,
      },
    ];
  }, [contract, partnership, t]);
}
